import { Link as GatsbyI18nextLink } from "gatsby-plugin-react-i18next";
import { forwardRef } from "react";
import { Link as ThemeUiLink } from "theme-ui";
import { match, P } from "ts-pattern";

import type { ThemeUIStyleObject } from "@theme/components/ThemeUi";
import type * as linkVariants from "@theme/themes/ms5/variants/links";
import type { GatsbyLinkProps } from "gatsby";
import type { AnchorHTMLAttributes } from "react";

export interface MsLinkProps
  extends AnchorHTMLAttributes<HTMLAnchorElement>, GatsbyLinkProps<HTMLLinkElement> {
  variant?: keyof typeof linkVariants;
  /** Theme UI JSX pragma */
  sx?: ThemeUIStyleObject;
  className?: string;
}

/**
 * MsLink
 *
 * Note this component is shadowed in gatsby-site to add
 * ability to use gatsby-plugin-react-i18next
 *
 * Any changes should also be added to gatsby-site version
 */
export const MsLink = forwardRef<HTMLAnchorElement, MsLinkProps>(function MsLink(
  { children, to, sx, className, variant, ...rest },
  ref,
) {
  // Must add variant via sx prop since its not a theme-ui link
  const themeUiVariant = match(variant)
    .with(P.string.startsWith("links."), (v) => v)
    .with(P.string, (v) => `links.${v}`)
    .otherwise(() => "links.link");

  const externalLink = to.startsWith("http");
  const mailtoLink = to.startsWith("mailto:");
  // if error boundary active, Gatsby Link no longer works
  // if external link, use ThemeUILink
  if (externalLink || mailtoLink) {
    return (
      <ThemeUiLink
        ref={ref}
        href={to}
        sx={{ variant: themeUiVariant, ...sx }}
        className={className}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </ThemeUiLink>
    );
  } else {
    return (
      <GatsbyI18nextLink
        ref={ref}
        to={to}
        sx={{ variant: themeUiVariant, ...sx }}
        className={className}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </GatsbyI18nextLink>
    );
  }
});
